import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

const RUBRIC_LABELS = {
  claim_clarity: "Claim Clarity",
  evidence_quality: "Evidence Quality",
  logical_consistency: "Logical Consistency",
  nuance: "Nuance",
  counterargument_awareness: "Counterargument Awareness",
  intellectual_humility: "Intellectual Humility",
  original_voice: "Original Voice",
};

function ScoreBar({ value }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`h-2 w-7 rounded-sm transition-colors ${n <= value ? "bg-ember-500" : "bg-white/10"}`}
        />
      ))}
    </div>
  );
}

export default function Debate() {
  const { debateId } = useParams();
  const navigate = useNavigate();
  const [debate, setDebate] = useState(null);
  const [response, setResponse] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const stageRef = useRef(null);

  useEffect(() => {
    api.get(`/debate/${debateId}`).then(({ data }) => setDebate(data)).catch(() => {
      toast.error("Could not load this debate.");
      navigate("/dashboard");
    });
  }, [debateId, navigate]);

  useEffect(() => {
    stageRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [debate?.status]);

  if (!debate) {
    return (
      <div className="min-h-screen bg-forge-900 text-forge-100 flex items-center justify-center">
        <div className="text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Opening the dialogue…</div>
      </div>
    );
  }

  const submit = async () => {
    const text = response.trim();
    if (text.length < 5) {
      toast.error("Write a longer response.");
      return;
    }
    const isFinalSubmit = debate.status === "followed_up";
    setSubmitting(true);
    try {
      const endpoint = debate.status === "challenged"
        ? `/debate/${debateId}/respond`
        : `/debate/${debateId}/respond_followup`;
      const { data } = await api.post(endpoint, { response: text });
      setDebate(data);
      setResponse("");
      if (data.status === "completed") {
        toast.success(data.fallback_used ? "Report card ready using a fallback." : "Synthesis ready.");
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.detail ||
        (isFinalSubmit
          ? "Your final response could not be completed. Please try again."
          : "Could not continue the debate.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Debate Mentor · Socratic Dialogue</div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight mb-2" data-testid="debate-title">{debate.reading_title}</h1>
        <p className="text-sm text-forge-100/50 mb-10">A worthy sparring partner. Never a judge. Never an answer machine.</p>

        {/* Your reflections (collapsed context) */}
        <details className="mb-10 border border-white/10 rounded-sm">
          <summary className="cursor-pointer px-5 py-3 text-xs uppercase tracking-[0.3em] text-forge-100/60 hover:text-ember-400">Your earlier reflections</summary>
          <div className="px-5 pb-5 pt-1 space-y-3 text-sm text-forge-100/75">
            {Object.entries(debate.user_reflections || {}).map(([k, v]) => (
              v ? (
                <div key={k}>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-gold-500 mb-1">{k}</div>
                  <p className="font-reading">{v}</p>
                </div>
              ) : null
            ))}
          </div>
        </details>

        {/* Challenge 1 */}
        <Turn label="Challenge" body={debate.challenge_question} accent="ember" />
        {debate.user_response && <Turn label="Your response" body={debate.user_response} accent="muted" />}
        {debate.followup_question && <Turn label="Follow-up" body={debate.followup_question} accent="ember" />}
        {debate.user_followup && <Turn label="Your response" body={debate.user_followup} accent="muted" />}

        {debate.status === "synthesizing" && (
          <div ref={stageRef} className="mt-6 border border-ember-500/25 bg-ember-500/8 px-5 py-4 text-sm text-forge-100/75">
            Generating your report card. Your final response has been saved.
          </div>
        )}

        {(debate.status === "challenged" || debate.status === "followed_up") && (
          <div ref={stageRef} className="mt-4">
            <label className="block text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-3">
              {debate.status === "challenged" ? "Your response to the challenge" : "Your response to the follow-up"}
            </label>
            <textarea
              rows={6}
              value={response}
              onChange={(e) => setResponse(e.target.value)}
              placeholder="Reason it through. Cite evidence. Allow yourself to be uncertain."
              disabled={submitting}
              className="w-full bg-transparent border-b border-white/15 focus:border-ember-500 outline-none py-3 text-forge-100 placeholder-forge-100/30 font-reading text-lg leading-relaxed transition-colors resize-none"
              data-testid="debate-response-input"
            />
            <button
              onClick={submit}
              disabled={submitting}
              className="mt-4 px-6 py-2.5 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
              data-testid="debate-submit"
            >
              {submitting
                ? (debate.status === "followed_up" ? "Generating report card..." : "Listening...")
                : (debate.status === "challenged" ? "Send response" : "Conclude dialogue")}
            </button>
          </div>
        )}

        {debate.status === "completed" && (
          <div ref={stageRef} className="mt-12 space-y-12">
            {/* Synthesis */}
            <section>
              <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-3">Balanced Synthesis</div>
              <p className="font-reading text-lg sm:text-xl leading-relaxed text-forge-100/90 border-l-2 border-ember-500 pl-6" data-testid="debate-synthesis">
                {debate.synthesis}
              </p>
            </section>

            {/* Reasoning Report Card */}
            {debate.report_card && (
              <section data-testid="report-card">
                <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
                  <div>
                    <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">Reasoning Report Card</div>
                    <h2 className="font-serif text-3xl sm:text-4xl tracking-tight">{debate.report_card.reasoning_level}</h2>
                    {debate.fallback_used && (
                      <div className="mt-3 inline-flex border border-gold-500/35 bg-gold-500/10 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-gold-400">
                        Fallback report card generated.
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-1">Overall</div>
                    <div className="font-serif text-5xl text-ember-400" data-testid="report-card-overall">{debate.report_card.overall_score}</div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/40 mt-1">out of 100</div>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-4 mb-8" data-testid="report-card-categories">
                  {[
                    ["evidence_use", "Evidence Use"],
                    ["logical_consistency", "Logical Consistency"],
                    ["relevance", "Relevance"],
                    ["counterargument_engagement", "Counterargument Engagement"],
                    ["civility_and_tone", "Civility and Tone"],
                    ["intellectual_humility", "Intellectual Humility"],
                  ].map(([k, label]) => (
                    <div key={k} className="flex items-center justify-between gap-4">
                      <span className="text-sm text-forge-100/85">{label}</span>
                      <div className="flex items-center gap-3 flex-1 max-w-[180px]">
                        <div className="flex-1 h-1.5 bg-white/8 rounded-sm overflow-hidden">
                          <div className="h-full bg-ember-500" style={{ width: `${(debate.report_card[k] / 10) * 100}%` }} />
                        </div>
                        <span className="font-mono text-xs text-forge-100/60 w-10 text-right">{debate.report_card[k]}/10</span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid sm:grid-cols-2 gap-px bg-white/5 mb-6">
                  <div className="bg-forge-900 p-6">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-3">Strengths</div>
                    <ul className="space-y-2">
                      {(debate.report_card.strengths || []).map((s, i) => (
                        <li key={i} className="flex gap-2 text-sm text-forge-100/85"><span className="text-ember-500 mt-1">◆</span><span className="font-reading">{s}</span></li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-forge-900 p-6">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-3">Growth Opportunities</div>
                    <ul className="space-y-2">
                      {(debate.report_card.growth_opportunities || []).map((s, i) => (
                        <li key={i} className="flex gap-2 text-sm text-forge-100/85"><span className="text-gold-500 mt-1">◇</span><span className="font-reading">{s}</span></li>
                      ))}
                    </ul>
                  </div>
                </div>

                {debate.report_card.coach_feedback && (
                  <div className="border-l-2 border-gold-500 pl-5 py-1">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-2">Thinking Coach</div>
                    <p className="font-reading text-base sm:text-lg leading-relaxed text-forge-100/90" data-testid="report-card-coach">
                      {debate.report_card.coach_feedback}
                    </p>
                  </div>
                )}
              </section>
            )}

            {/* Rubric */}
            <section>
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-2">Reasoning Rubric</div>
              <p className="text-xs text-forge-100/50 mb-6 max-w-xl">
                Evaluates the structure of your reasoning, not your conclusions or beliefs.
              </p>
              <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5" data-testid="debate-rubric">
                {Object.entries(debate.rubric || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-6">
                    <span className="text-sm text-forge-100/80">{RUBRIC_LABELS[k] || k}</span>
                    <div className="flex items-center gap-3">
                      <ScoreBar value={v} />
                      <span className="font-mono text-xs text-forge-100/50 w-6 text-right">{v}/5</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Feedback */}
            <section className="grid sm:grid-cols-2 gap-px bg-white/5">
              <FeedbackCard label="Strengths" body={debate.feedback?.strengths} />
              <FeedbackCard label="Growth Opportunity" body={debate.feedback?.growth_opportunity} />
              <div className="bg-forge-900 p-6 sm:col-span-2">
                <div className="text-[10px] tracking-[0.3em] uppercase text-gold-500 mb-3">One more question</div>
                <p className="font-serif text-xl sm:text-2xl text-forge-100 leading-snug" data-testid="debate-next-question">
                  {debate.feedback?.next_question}
                </p>
              </div>
            </section>

            <div className="flex flex-wrap gap-4 pt-4">
              <Link to="/progress" className="px-6 py-2.5 bg-ember-500 hover:bg-ember-400 text-forge-900 font-semibold tracking-wide rounded-sm transition-all">
                View Growth
              </Link>
              {debate.session_id && (
                <>
                  <Link to={`/reading/${debate.session_id}#journal`} className="px-6 py-2.5 border border-gold-500/50 hover:border-gold-500 hover:text-gold-400 text-sm tracking-wide rounded-sm transition-all">
                    Write Journal Entry
                  </Link>
                  <Link to={`/reading/${debate.session_id}`} className="px-6 py-2.5 border border-white/15 hover:border-ember-500 hover:text-ember-400 text-sm tracking-wide rounded-sm transition-all">
                    ← Back to reading
                  </Link>
                </>
              )}
              {!debate.session_id && debate.research_id && (
                <Link to={`/research/${debate.research_id}`} className="px-6 py-2.5 border border-white/15 hover:border-ember-500 hover:text-ember-400 text-sm tracking-wide rounded-sm transition-all">
                  ← Back to Research
                </Link>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function Turn({ label, body, accent }) {
  const accentClass = accent === "ember" ? "text-ember-500" : "text-forge-100/40";
  const borderClass = accent === "ember" ? "border-ember-500" : "border-white/15";
  return (
    <div className={`mb-8 border-l-2 ${borderClass} pl-5`}>
      <div className={`text-[10px] uppercase tracking-[0.3em] mb-2 ${accentClass}`}>{label}</div>
      <p className="font-reading text-lg leading-relaxed text-forge-100/90 whitespace-pre-wrap">{body}</p>
    </div>
  );
}

function FeedbackCard({ label, body }) {
  return (
    <div className="bg-forge-900 p-6">
      <div className="text-[10px] tracking-[0.3em] uppercase text-ember-500 mb-3">{label}</div>
      <p className="text-sm text-forge-100/80 leading-relaxed">{body}</p>
    </div>
  );
}
